import { get, flatten, some } from 'lodash';
import { CHART_ROLES, isTableType } from 'constants/chart-roles';
import { find, includes, cloneDeep, each, filter, keys, isEmpty } from 'lodash';
import {
  CHANNEL_TYPE_ENUM,
  INFERRED_TYPE_ENUM,
  INTENT_ENUM,
  SORT_CHANNEL_ENUM,
  SORT_ORDER_ENUM,
} from 'constants/chart-channels';
import {
  isMultiSeriesName,
  getMultiSeriesParentChannel,
} from 'utils/chart/multi-series-name';
import { generateChannelLabel } from 'utils/chart/channel-labels';

const MULTI_SERIES_AGG_AXIS_TITLE = 'Value';
const MULTI_SERIES_LEGEND_TITLE = 'Series';
const VEGA_SPEC_DATA_ENCODING = { name: 'values' };

/**
 * updates the channels object with titles for axis and legends based on
 * the channel field and aggregate and reductions for that channel.
 *
 * Modifies the channels object.
 *
 * Has to run AFTER generateTitles
 *
 * @param  {Object} channels      channel encoding object
 */
function generateChartLabels(channels) {
  each(channels, encoding => {
    encoding.axis = {
      title: encoding.title,
    };
    encoding.legend = {
      title: encoding.title,
    };
  });
}

function generateTitles(channels, reductions, customisations) {
  each(channels, (encoding, channel) => {
    const labelOverride = get(
      customisations,
      `channels.${channel}.labelOverride.value`
    );
    encoding.title =
      labelOverride || generateChannelLabel(encoding, channel, reductions);
  });
}

function getNestedChannels(chartType) {
  const chartConfig = find(CHART_ROLES, { name: chartType });

  const nestedChannels = chartConfig.channels
    .map(ch => ch.nestedChannels)
    .filter(ch => !!ch);
  return flatten(nestedChannels);
}

function hasNestedChannels(chartType, channel) {
  const chartConfig = find(CHART_ROLES, { name: chartType });

  return some(
    chartConfig.channels,
    ch => ch.nestedChannels && ch.name === channel
  );
}

// processing sorting and limiting related settings before generating spec
function handleSortingAndLimiting(channels, chartType, reductions, metadata) {
  const { channels: roleChannels } = find(CHART_ROLES, { name: chartType });
  const sortByChannel = find(
    channels,
    ({ inferredType, transformedType }, channelName) => {
      const type = isEmpty(reductions[channelName])
        ? inferredType
        : transformedType;
      return (
        type === INFERRED_TYPE_ENUM.STRING &&
        roleChannels.find(
          ({ channelType, intent, name }) =>
            name === channelName &&
            channelType === CHANNEL_TYPE_ENUM.CATEGORY &&
            intent === INTENT_ENUM.AXIS_CATEGORY
        )
      );
    }
  );

  // disable sorting on channels that have binning enabled
  // otherwise VEGA will sort axis in natural order which breaks binning
  each(channels, (c, name) => {
    if (c !== sortByChannel) {
      if (c.isBinning) {
        c.sort = false; // disable sort if binning is on
      } else {
        c.sort = undefined; // remove sort settings and let Vega adds sensible default
      }
    } else {
      c.channelName = name;
    }
  });

  // update channel's sort setting if can find a channel to sort by
  if (sortByChannel) {
    const { axis = SORT_CHANNEL_ENUM.VALUE, order = SORT_ORDER_ENUM.DESC } =
      sortByChannel.sort || {};
    const isSortByValue = axis === SORT_CHANNEL_ENUM.VALUE;

    const categoryField = sortByChannel.channelName;
    const valueField = roleChannels.find(
      c => c.intent === INTENT_ENUM.AXIS_VALUE
    ).name;
    const sortField = isSortByValue ? `_${valueField}_sort` : categoryField;

    // normalise limiting
    const { isLimiting, limitingSize } = sortByChannel;
    const limit = (isLimiting && parseInt(limitingSize, 10)) || 0;

    // these values will be used to generate transforms
    // that are needed for sorting and limiting
    sortByChannel.sorting = {
      order,
      isSortByValue,
      categoryField,
      valueField,
      sortField,
      limit: limit > 0 && limit < 100 ? limit : metadata.maxDocLimit,
    };
  }
}

/**
 * removes all channels with a multi-series name and adds a channel
 * with "series" intent instead. Also re-labels the series channel's and
 * parent channel's axis and legends.
 *
 * Modifies the channels object.
 *
 * @param  {Object} channels        channel encoding object
 * @param  {String} chartType       name of the chart role, e.g. "Stacked Bar"
 * @param  {String} customisations  customisations object
 */
function handleMultiSeriesChart(channels, chartType, customisations) {
  const multiSeriesChannels = filter(keys(channels), isMultiSeriesName);
  if (isEmpty(multiSeriesChannels)) {
    return;
  }
  // remove all _series_x channels
  each(channels, (encoding, channel) => {
    if (includes(multiSeriesChannels, channel)) {
      delete channels[channel];
    }
  });

  // add a series channel (typically "color"), given by the intent of the chart role
  const roleChannels = find(CHART_ROLES, { name: chartType }).channels;
  const seriesChannel = find(roleChannels, { intent: 'series' }).name;

  channels[seriesChannel] = {
    field: seriesChannel,
    type: 'nominal',
    axis: {
      title: MULTI_SERIES_LEGEND_TITLE,
    },
    legend: {
      title: MULTI_SERIES_LEGEND_TITLE,
    },
  };

  // rename the axis / legend labels of the multi-series channel to "value"
  const parentChannel = getMultiSeriesParentChannel(multiSeriesChannels[0]);
  const labelOverride = get(
    customisations,
    `axes.${parentChannel}.labelOverride.value`
  );
  channels[parentChannel].axis.title =
    labelOverride || MULTI_SERIES_AGG_AXIS_TITLE;
  channels[parentChannel].legend.title =
    labelOverride || MULTI_SERIES_AGG_AXIS_TITLE;
}

/**
 * generates the base spec for a chart by choosing the specFn of the correct chart type
 * and calling it with the channels object.
 *
 * The specFn receives a reference to the `values` dataset in its data array.
 *
 * @param  {String} chartType       name of the chart role, e.g. "Stacked Bar"
 * @param  {Object} channels        channels object
 * @param  {Object} customisations  customisations object
 * @return {Object}                 a spec
 */
function generateChartSpec(chartType, channels, customisations) {
  let spec;

  // find the specFn according to chartType
  const { specFn } = find(CHART_ROLES, { name: chartType });

  // start with the result of the spec function applied to the channels encoding.
  spec = { ...specFn(channels, customisations) };

  // Vega spec requires the 'name' field in data to render a chart
  if (!spec.data || spec.data.length === 0) {
    spec.data = [VEGA_SPEC_DATA_ENCODING];
  } else if (spec.data[0].name !== VEGA_SPEC_DATA_ENCODING.name) {
    spec = {
      ...spec,
      data: [{ ...VEGA_SPEC_DATA_ENCODING }, ...spec.data],
    };
  }

  return spec;
}

/**
 * generates the base spec for a table by choosing the specFn of the correct table type
 * and calling it with the channels object.
 *
 * The specFn receives a reference to the `values` dataset in its data array.
 *
 * @param  {String} tableType       name of the table role, e.g. "Data Table"
 * @param  {Object} channels        channels object
 * @param  {Object} customisations  customisations object
 * @param  {Object} meta            additional config used for rendering (eg: sortModel)
 * @return {Object}                 a spec
 */
function generateTableSpec(tableType, channels, customisations, meta) {
  const { specFn } = find(CHART_ROLES, { name: tableType });
  return specFn(channels, meta);
}

/**
 * helper function that takes a chart type, spec type and the channels and
 * reductions objects (as constructed via the chart builder) and creates
 * a chart builder spec (Vega | AgGrid).
 *
 * @param  {String} chartType       name of the chart role, e.g. "Stacked Bar" //TODO: Should it be specType?
 * @param  {Object} channels        channels object
 * @param  {Object} reductions      reductions object
 * @param  {Object} customisations  all applicable customisations object
 * @param  {Object} meta            additional config used for rendering (eg: sortModel)
 * @param  {Object} metadata        metadata for data of a chart
 * @return {Object}                 An encoded chart-builder spec
 */
function encodeSpec(
  chartType,
  channels,
  reductions,
  customisations,
  meta,
  metadata
) {
  // create a clone of the channels object for spec encoding
  channels = cloneDeep(channels);

  // stages that are generic across both charts and AgGrid

  // add a 'title' to a channel, used in other stages
  generateTitles(channels, reductions, customisations);

  if (isTableType(chartType)) {
    // like charts, adding the titles to the channels object directly

    return generateTableSpec(chartType, channels, customisations, meta);
  }

  // add labels to the channels object, using the channel title
  generateChartLabels(channels);

  handleSortingAndLimiting(channels, chartType, reductions, metadata);

  // in case of multi-series, remove all _series_x channels and add a
  // series channel.
  handleMultiSeriesChart(channels, chartType, customisations);

  // generate the base spec for the given chart type (calls specFn)
  return generateChartSpec(chartType, channels, customisations);
}

export default encodeSpec;
export {
  encodeSpec,
  generateChartLabels,
  getNestedChannels,
  hasNestedChannels,
  handleSortingAndLimiting,
  generateChartSpec,
  generateTitles,
};
